import { Link, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, BellRing, Building2, CalendarCheck, Database, FileSignature, House, Landmark, PencilRuler, Scale, Search, ShieldCheck, TrendingUp, Users, } from "lucide-react";
import { Button } from "../components/ui/button";
import { BoroughSelect } from "../components/site/borough-select";
import { AnimatedMapBackground } from "../components/site/animated-map-background";
import { PredictionList, BlockLotResults } from "../components/site/search-results";
import { useAddressSearch, useBlockLotSearch } from "../hooks/useSiteSearch";
import { Section, SectionHeading } from "../components/site/section";
import { asset } from "../lib/assets";

const dashboardOverview = { url: asset("dashboard-280-broadway.webp") };
const infrastructureBis = { url: asset("infrastructure-work-applications-bis-v2.webp") };
const violertsSpinner = { url: asset("violerts-spinner-2.svg") };

const industries = [
  {
    icon: House,
    name: "Property Owners",
    example: "A DOB violation lands on your building overnight. Violerts alerts you before the hearing window opens — cure path and penalty exposure already laid out.",
  },
  {
    icon: Building2,
    name: "Management Companies",
    example: "Every building you manage feeds one dashboard, so a missed HPD inspection anywhere in the portfolio flags itself — no more tracking agency mail.",
  },
  {
    icon: FileSignature,
    name: "Expediters & Filing Reps",
    example: "New DOB NOW filings surface when Violerts detects new or updated records — draft the response while the case is open instead of discovering it after the penalty posts.",
  },
  {
    icon: PencilRuler,
    name: "Architects & Engineers",
    example: "See every permit, work application and open violation tied to a property before you submit — so your filings never collide with an open case.",
  },
  {
    icon: TrendingUp,
    name: "Real Estate Investors",
    example: "Run a full agency history on any address before you bid — decades of violations, penalties and permits pulled together in one report.",
  },
  {
    icon: Scale,
    name: "Law Firms",
    example: "Pull complete violation histories and timelines for litigation or due diligence without waiting on agency records requests.",
  },
  {
    icon: Users,
    name: "Co-ops & Condo Boards",
    example: "Board members see the same alerts as management — when an ECB hearing date slips, the whole board knows before the fine does.",
  },
  {
    icon: Landmark,
    name: "Lenders & Insurers",
    example: "Monitor collateral across the portfolio — a new stop-work order or lien flags the asset before it ever reaches your risk review.",
  },
];
function MonitoringBadge() {
  return (<div className="inline-flex items-center gap-3 rounded-full bg-ink-foreground py-1.5 pl-2 pr-5 shadow-lift">
    <img src={violertsSpinner.url} alt="" aria-hidden="true" className="h-8 w-8 shrink-0" />
    <span className="text-xs font-semibold leading-snug text-ink sm:text-sm">
      Actively monitoring{" "}
      <span className="font-bold">10+ agencies</span> and{" "}
      <span className="font-bold">150+ public databases</span>
    </span>
  </div>);
}
function Home() {
  const focus = new URLSearchParams(useLocation().search).get("focus");
  const [searchMode, setSearchMode] = useState("address");
  const addressRef = useRef(null);
  const addressSearch = useAddressSearch();
  const blockLotSearch = useBlockLotSearch();
  useEffect(() => {
    if (focus === "address") {
      setSearchMode("address");
      if (addressRef.current) {
        addressRef.current.focus();
        addressRef.current.select();
      }
    }
  }, [focus]);
  const submitHeroSearch = (e) => {
    e.preventDefault();
    if (searchMode === "blockLot") {
      blockLotSearch.search();
    }
  };
  return (<>
    {/* z-20 keeps the results list above the next section; map clips itself so the hero need not overflow-hidden. */}
    <section className="relative z-20 flex min-h-screen flex-col justify-center surface-ink">
      <AnimatedMapBackground />
      <div className="relative mx-auto w-full max-w-6xl px-5 py-32 text-center md:py-40">
        <div data-map-protected="exact" className="mx-auto w-fit max-w-full">
          <MonitoringBadge />
        </div>
        <h1 data-map-protected="exact" className="mx-auto mt-7 max-w-3xl text-4xl font-bold leading-[1.05] md:text-6xl">
          Know about every violation before it costs you.
        </h1>
        <p data-map-protected="exact" className="mx-auto mt-6 max-w-xl text-lg leading-relaxed opacity-80">
          Violerts monitors every agency touching your buildings, notifies you when it detects changes,
          and walks you through resolution — so a routine notice never becomes a five-figure
          penalty.
        </p>
        {/* Results sit outside the backdrop-blur card so the blur layer cannot paint over them. */}
        <div
          ref={searchMode === "blockLot" ? blockLotSearch.containerRef : addressSearch.containerRef}
          data-map-protected="exact"
          className="relative mx-auto mt-9 w-full max-w-3xl text-left"
        >
          <div className="flex flex-col gap-3 rounded-lg border border-ink-foreground/20 bg-ink-foreground/10 p-3 shadow-lift backdrop-blur-sm">
            <div className="flex gap-1 self-start rounded-md bg-ink-foreground/10 p-1">
              {[
                ["address", "Address"],
                ["blockLot", "Block / Lot"],
              ].map(([mode, label]) => (<button key={mode} type="button" onClick={() => setSearchMode(mode)} className={`rounded px-3 py-1.5 text-xs font-semibold transition-colors ${searchMode === mode
                ? "bg-signal text-ink"
                : "text-ink-foreground/70 hover:text-ink-foreground"}`}>
                {label}
              </button>))}
            </div>
            <form className="flex flex-col gap-3 sm:flex-row sm:items-center" onSubmit={submitHeroSearch}>
              {searchMode === "address" ? (<>
                <label className="sr-only" htmlFor="nyc-address-search">
                  Search a NYC address
                </label>
                <div className="flex min-w-0 flex-1 items-center gap-3 rounded-md bg-ink-foreground/10 px-4 text-ink-foreground">
                  <Search className="h-5 w-5 shrink-0 text-signal" aria-hidden="true" />
                  <input ref={addressRef} id="nyc-address-search" name="address" type="search" role="combobox" aria-expanded={addressSearch.showList} aria-controls="hero-address-list" aria-autocomplete="list" placeholder="Search a NYC address" autoComplete="off" value={addressSearch.value} onChange={addressSearch.onChange} onKeyDown={addressSearch.onKeyDown} onFocus={addressSearch.onFocus} className="h-14 min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-ink-foreground/60" />
                </div>
              </>) : (<>
                <div className="flex min-w-0 flex-1 items-center gap-4 rounded-md bg-ink-foreground/10 px-4 text-ink-foreground">
                  <label className="sr-only" htmlFor="hero-search-block">
                    Block
                  </label>
                  <input id="hero-search-block" name="block" type="text" inputMode="numeric" placeholder="Block" value={blockLotSearch.block} onChange={(e) => blockLotSearch.setBlock(e.target.value)} className="h-14 w-24 min-w-0 bg-transparent text-base outline-none placeholder:text-ink-foreground/60" />
                  <span className="h-6 w-px shrink-0 bg-ink-foreground/20" aria-hidden="true" />
                  <label className="sr-only" htmlFor="hero-search-lot">
                    Lot
                  </label>
                  <input id="hero-search-lot" name="lot" type="text" inputMode="numeric" placeholder="Lot" value={blockLotSearch.lot} onChange={(e) => blockLotSearch.setLot(e.target.value)} className="h-14 min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-ink-foreground/60" />
                  <span className="h-6 w-px shrink-0 bg-ink-foreground/20" aria-hidden="true" />
                  <label className="sr-only" htmlFor="hero-search-borough">
                    Borough
                  </label>
                  <BoroughSelect id="hero-search-borough" onInk value={blockLotSearch.borough} onValueChange={blockLotSearch.setBorough} className="h-14 w-36 min-w-0 shrink-0 border-0 bg-transparent px-0 text-sm text-ink-foreground/80 focus:ring-ink-foreground/30" />
                </div>
              </>)}
              <Button type="submit" variant="signal" size="lg" className="h-14 shrink-0 px-7">
                {searchMode === "address" ? (<>
                  Search address <ArrowRight />
                </>) : (<>
                  Search block / lot <ArrowRight />
                </>)}
              </Button>
            </form>
          </div>
          {searchMode === "address" ? (
            <PredictionList search={addressSearch} id="hero-address-list" />
          ) : (
            <BlockLotResults search={blockLotSearch} />
          )}
        </div>
      </div>
    </section>

    <section className="border-b border-border px-5 py-20 md:py-24">
      <div className="mx-auto max-w-7xl">
        <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)] md:grid-cols-[minmax(0,3fr)_minmax(0,9fr)]">
          <div className="order-2 md:order-1">
            <h2 className="text-3xl font-bold leading-tight md:text-4xl lg:text-5xl">
              Monitoring, records and resolution — in one dashboard.
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Live alerts, every violation on file, and a clear cure path for each
              one — all tied to the address it belongs to.
            </p>
          </div>
          <div className="order-1 md:order-2">
            <div className="rounded-2xl bg-ink p-1.5 shadow-lift">
              <img src={dashboardOverview.url} alt="Violerts property dashboard for 280 Broadway showing property information, penalties and refunds, and property images" width={1573} height={784} loading="lazy" className="w-full rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="border-b border-border px-5 py-20 md:py-24">
      <div className="mx-auto max-w-7xl">
        <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,8fr)_minmax(0,4fr)] md:grid-cols-[minmax(0,9fr)_minmax(0,3fr)]">
          <div className="order-1 md:order-1">
            <div className="rounded-2xl bg-ink p-1.5 shadow-lift">
              <img src={infrastructureBis.url} alt="Violerts Infrastructure tab showing Work Application (BIS) filings with permit status" width={1572} height={659} loading="lazy" className="w-full rounded-xl" />
            </div>
          </div>
          <div className="order-2 md:order-2">
            <h2 className="text-3xl font-bold leading-tight md:text-4xl lg:text-5xl">
              Every record, one tab away
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Every permit, job filing and deed tied to your address, searchable in seconds.
            </p>
          </div>
        </div>
      </div>
    </section>

    <Section className="surface-ink">
      <SectionHeading title="One platform for monitoring, research and resolution." description="Everything your team currently does across a dozen agency portals, spreadsheets and inboxes." align="center" onInk />
      <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-6">
        <Link to="/alerts" className="group relative overflow-hidden rounded-[2.5rem] border border-ink-foreground/10 bg-surface/40 p-8 transition-all hover:border-signal/30 md:col-span-3">
          <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-signal/10 blur-3xl transition-colors group-hover:bg-signal/20" />
          <div className="relative z-10">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-signal/10">
              <BellRing className="h-6 w-6 text-signal" />
            </div>
            <h3 className="mt-6 text-2xl font-bold">Live Alerts</h3>
            <p className="mt-3 max-w-md text-ink-foreground/60">
              Email, SMS and in-app notifications when Violerts detects a violation, complaint, permit or inspection on your portfolio.
            </p>
          </div>
        </Link>

        <Link to="/violation-resolution" className="group relative overflow-hidden rounded-[2.5rem] border border-ink-foreground/10 bg-surface/40 p-8 transition-all hover:border-signal/30 md:col-span-3">
          <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-signal/10 blur-3xl transition-colors group-hover:bg-signal/20" />
          <div className="relative z-10">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-signal/10">
              <ShieldCheck className="h-6 w-6 text-signal" />
            </div>
            <h3 className="mt-6 text-2xl font-bold">Violation Solutions</h3>
            <p className="mt-3 max-w-md text-ink-foreground/60">
              Step-by-step cure paths, required filings, hearing dates and vetted specialists to close things out fast.
            </p>
          </div>
        </Link>

        <Link to="/property-data" className="group relative overflow-hidden rounded-[2.5rem] border border-ink-foreground/10 bg-surface/40 p-8 transition-all hover:border-signal/30 md:col-span-2">
          <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-signal/10 blur-3xl transition-colors group-hover:bg-signal/20" />
          <div className="relative z-10">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-signal/10">
              <Landmark className="h-5 w-5 text-signal" />
            </div>
            <h3 className="mt-6 text-xl font-bold">Connected Data</h3>
            <p className="mt-3 text-sm leading-relaxed text-ink-foreground/60">
              DOB, HPD, ECB/OATH, FDNY, DEP, DOT, and more, normalized into one consistent record per property.
            </p>
          </div>
        </Link>

        <div className="flex flex-col items-center justify-center rounded-[2.5rem] bg-signal p-8 text-center text-ink md:col-span-2">
          <span className="text-xs font-bold uppercase tracking-widest opacity-70">AROUND THE CLOCK</span>
          <div className="mt-2 text-6xl font-extrabold tracking-tighter md:text-7xl">24/7</div>
          <p className="mt-2 max-w-[12rem] text-sm font-medium opacity-80">
            Monitoring around the clock.
          </p>
        </div>

        <Link to="/reports" className="group relative overflow-hidden rounded-[2.5rem] border border-ink-foreground/10 bg-surface/40 p-8 transition-all hover:border-signal/30 md:col-span-2">
          <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-signal/10 blur-3xl transition-colors group-hover:bg-signal/20" />
          <div className="relative z-10">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-signal/10">
              <Building2 className="h-5 w-5 text-signal" />
            </div>
            <h3 className="mt-6 text-xl font-bold">Property Reports</h3>
            <p className="mt-3 text-sm leading-relaxed text-ink-foreground/60">
              Obtain polished property reports for acquisitions, refinancing, insurance renewals and internal portfolio reviews.
            </p>
          </div>
        </Link>

        <Link to="/compliance" className="group relative overflow-hidden rounded-[2.5rem] border border-ink-foreground/10 bg-surface/40 p-8 transition-all hover:border-signal/30 md:col-span-3">
          <div className="absolute -left-12 -bottom-12 h-48 w-48 rounded-full bg-signal/5 blur-3xl" />
          <div className="relative z-10 flex flex-col items-start gap-4 md:flex-row md:items-center">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-signal/10">
              <CalendarCheck className="h-6 w-6 text-signal" />
            </div>
            <div>
              <h3 className="text-2xl font-bold">Compliance Tracking</h3>
              <p className="mt-2 max-w-2xl text-ink-foreground/60">
                Track inspections, local-law deadlines and portfolio-wide obligations so nothing falls through the cracks.
              </p>
            </div>
          </div>
        </Link>

        <Link to="/historical-data" className="group relative overflow-hidden rounded-[2.5rem] border border-ink-foreground/10 bg-surface/40 p-8 transition-all hover:border-signal/30 md:col-span-3">
          <div className="absolute -right-12 -bottom-12 h-48 w-48 rounded-full bg-signal/5 blur-3xl" />
          <div className="relative z-10 flex flex-col items-start gap-4 md:flex-row md:items-center">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-signal/10">
              <Database className="h-6 w-6 text-signal" />
            </div>
            <div>
              <h3 className="text-2xl font-bold">Historical Records</h3>
              <p className="mt-2 max-w-2xl text-ink-foreground/60">
                Historical maps, building photos, occupancy records, permits, violations and penalties spanning decades — pulled together for diligence, underwriting, litigation and capital planning.
              </p>
            </div>
          </div>
        </Link>
      </div>
      <div className="mt-10 flex justify-center">
        <Button asChild variant="signal" size="lg">
          <Link to="/features">
            Explore all features <ArrowRight />
          </Link>
        </Button>
      </div>
    </Section>

    <Section className="border-t border-border">
      <SectionHeading align="center" title="Built for everyone responsible for a building." description="Whether you own two walk-ups or manage ten thousand units, the exposure is the same — and so is the fix." />
      <ul className="mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {industries.map((i) => (<li key={i.name} className="group relative min-h-[168px] overflow-hidden rounded-xl border border-border bg-card shadow-card transition-all hover:-translate-y-0.5 hover:shadow-lift">
          <div className="flex h-full items-center gap-3 p-6">
            <i.icon className="h-6 w-6 shrink-0 text-foreground" />
            <span className="text-base font-semibold">{i.name}</span>
          </div>
          <div className="absolute inset-0 flex translate-y-2 flex-col justify-center rounded-xl bg-ink p-5 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-signal">How Violerts helps</span>
            <p className="mt-3 text-[13px] leading-relaxed text-ink-foreground/80">{i.example}</p>
          </div>
        </li>))}
      </ul>
    </Section>

    <Section className="surface-ink">
      <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
        <div className="max-w-xl">
          <h2 className="text-3xl font-bold md:text-4xl">
            Add your first property in under two minutes.
          </h2>
          <p className="mt-4 opacity-75">
            Free to start, no card required. Add your property to start monitoring for agency updates.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button asChild variant="signal" size="lg">
            <Link to="/sign-up">Create free account</Link>
          </Button>
          <Button asChild variant="onInk" size="lg">
            <Link to="/contact">Contact us{"\n"}</Link>
          </Button>
        </div>
      </div>
    </Section>
  </>);
}

export default Home;
